import { useEffect, useRef, useState, type KeyboardEvent, type TouchEvent } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { HorsePhoto } from '../data/site'
import ResponsiveImage from './ResponsiveImage'

type PhotoLightboxProps = {
  photos: HorsePhoto[]
  active: number
  onChange: (index: number) => void
  onClose: () => void
  label: string
  subject?: string
}

type Direction = 'previous' | 'next'

export default function PhotoLightbox({
  photos,
  active,
  onChange,
  onClose,
  label,
  subject,
}: PhotoLightboxProps) {
  const closeButton = useRef<HTMLButtonElement>(null)
  const returnFocus = useRef<HTMLElement | null>(null)
  const activeRef = useRef(active)
  const onChangeRef = useRef(onChange)
  const onCloseRef = useRef(onClose)
  const touchStartX = useRef<number | null>(null)
  const [direction, setDirection] = useState<Direction>('next')

  activeRef.current = active
  onChangeRef.current = onChange
  onCloseRef.current = onClose

  const previous = () => {
    if (activeRef.current <= 0) return
    setDirection('previous')
    onChangeRef.current(activeRef.current - 1)
  }

  const next = () => {
    if (activeRef.current >= photos.length - 1) return
    setDirection('next')
    onChangeRef.current(activeRef.current + 1)
  }

  useEffect(() => {
    returnFocus.current = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeButton.current?.focus()

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
      } else if (event.key === 'ArrowLeft' && photos.length > 1) {
        event.preventDefault()
        previous()
      } else if (event.key === 'ArrowRight' && photos.length > 1) {
        event.preventDefault()
        next()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      returnFocus.current?.focus({ preventScroll: true })
    }
  }, [photos.length])

  useEffect(() => {
    const adjacent = [photos[active - 1], photos[active + 1]].filter(Boolean)
    adjacent.forEach((photo) => {
      const image = new Image()
      image.src = photo.src
    })
  }, [active, photos])

  const trapFocus = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return
    const controls = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('button:not([disabled]):not([tabindex="-1"])'),
    )
    if (!controls.length) return

    const first = controls[0]
    const last = controls[controls.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null
  }

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return
    const distance = event.changedTouches[0].clientX - touchStartX.current
    touchStartX.current = null
    if (Math.abs(distance) < 50) return
    if (distance > 0) previous()
    else next()
  }

  const photo = photos[active]
  if (!photo) return null

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onKeyDown={trapFocus}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        className="lightbox__backdrop"
        type="button"
        tabIndex={-1}
        onClick={() => onCloseRef.current()}
        aria-label="Close image viewer backdrop"
      />
      <button
        ref={closeButton}
        className="lightbox__close"
        type="button"
        onClick={() => onCloseRef.current()}
        aria-label={subject ? `Close photo of ${subject}` : 'Close image viewer'}
      >
        <X aria-hidden="true" />
      </button>
      {active > 0 && (
        <button className="lightbox__nav lightbox__nav--prev" type="button" onClick={previous} aria-label={subject ? `Previous photo of ${subject}` : 'Previous sanctuary photo'}>
          <ChevronLeft aria-hidden="true" />
        </button>
      )}
      <figure className={`lightbox__figure lightbox__figure--${direction}`} key={photo.src}>
        <ResponsiveImage photo={photo} loading="eager" fetchPriority="high" sizes="90vw" objectFit="contain" />
        {photo.caption && <figcaption>{photo.caption}</figcaption>}
      </figure>
      {active < photos.length - 1 && (
        <button className="lightbox__nav lightbox__nav--next" type="button" onClick={next} aria-label={subject ? `Next photo of ${subject}` : 'Next sanctuary photo'}>
          <ChevronRight aria-hidden="true" />
        </button>
      )}
      <span className="lightbox__count" role="status" aria-live="polite">{active + 1} / {photos.length}</span>
    </div>,
    document.body,
  )
}
